import { createFileRoute } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useState, useEffect } from 'react';
import { BackgroundWaves } from '@/components/BackgroundWaves';
import { MissionCard } from '@/components/about/MissionCard';
import LogoLight from '@/assets/knowblemind-icon-nobg-light.svg?react';
import LogoDark from '@/assets/knowblemind-icon-nobg-dark.svg?react';
import styles from './about.module.scss';

export const Route = createFileRoute('/about')({
  component: AboutPage,
});

function AboutPage() {
  const [copied, setCopied] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
  });

  // Listen for theme changes on the document element
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'data-theme') {
          const newTheme = document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
          setTheme(newTheme);
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });

    return () => observer.disconnect();
  }, []);

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText('support@knowblemind.com');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = 'support@knowblemind.com';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className={styles.about}>
      <BackgroundWaves />

      {/* Hero Section */}
      <section className={styles.about__hero}>
        <motion.div
          className={styles.about__logo}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          {theme === 'dark' ? <LogoLight /> : <LogoDark />}
        </motion.div>
        <motion.h1
          className={styles.about__title}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          About KnowbleMind
        </motion.h1>
        <motion.p
          className={styles.about__tagline}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          Where Learning Comes Alive
        </motion.p>
      </section>

      {/* Mission Section */}
      <section className={styles.about__mission}>
        <motion.h2
          className={styles.about__sectionTitle}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          Our Mission
        </motion.h2>
        <div className={styles.about__missionCards}>
          <MissionCard
            icon="book"
            title="Learn by Doing"
            description="KnowbleMind was created to support learning and facilitate engagement with material. We believe the best way to learn isn't just reading — it's actively testing your knowledge."
            delay={0.6}
          />
          <MissionCard
            icon="brain"
            title="Break Free from Passive Learning"
            description="Rather than passively engaging with endless tutorials or reading without testing, KnowbleMind helps you build real understanding through personalized quizzes and daily knowledge reviews."
            delay={0.8}
          />
        </div>
      </section>

      {/* Alpha Release Section */}
      <motion.section
        className={styles.about__alpha}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1 }}
      >
        <div className={styles.about__alphaBadge}>
          <span className={styles.about__alphaBadgeIcon}>🚀</span>
          <span className={styles.about__alphaBadgeText}>Alpha Release</span>
        </div>
        <h2 className={styles.about__alphaTitle}>We're Just Getting Started</h2>
        <p className={styles.about__alphaDescription}>
          KnowbleMind is currently in Alpha release. The free tier is available to everyone with:
        </p>
        <ul className={styles.about__alphaFeatures}>
          <li>10 quiz generations per month</li>
          <li>Up to 30 saved quizzes</li>
          <li>Basic question types</li>
          <li>Daily knowledge reviews</li>
        </ul>
        <div className={styles.about__alphaQuota}>
          <p className={styles.about__alphaQuotaText}>
            <strong>Need more quiz generations?</strong>
          </p>
          <p className={styles.about__alphaQuotaText}>
            Reach out to us at{' '}
            <button
              className={styles.about__emailLink}
              onClick={handleCopyEmail}
              title="Click to copy email"
            >
              support@knowblemind.com
            </button>
            {' '}and we'll work with you!
          </p>
        </div>
      </motion.section>

      {/* Contact Section */}
      <motion.section
        className={styles.about__contact}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.2 }}
      >
        <div className={styles.about__contactIcon}>
          <motion.svg
            viewBox="0 0 64 64"
            className={styles.about__contactSvg}
            whileHover={{ scale: 1.05 }}
          >
            {/* Envelope body */}
            <rect
              x="4" y="16" width="56" height="36" rx="4"
              fill="var(--ocean-400)"
              stroke="var(--ocean-600)"
              strokeWidth="2"
            />
            {/* Envelope flap */}
            <motion.path
              d="M4 20 L32 38 L60 20"
              fill="none"
              stroke="var(--ocean-600)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1, delay: 1.4 }}
            />
            {/* Inner fold lines */}
            <path
              d="M4 52 L24 36 M60 52 L40 36"
              fill="none"
              stroke="var(--ocean-500)"
              strokeWidth="1.5"
              opacity="0.5"
            />
          </motion.svg>
        </div>
        <h2 className={styles.about__contactTitle}>Get in Touch</h2>
        <p className={styles.about__contactDescription}>
          Have questions, suggestions, or feedback? We'd love to hear from you!
        </p>
        <motion.button
          className={styles.about__contactButton}
          onClick={handleCopyEmail}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {copied ? (
            <>
              <span className={styles.about__contactButtonIcon}>✓</span>
              Copied!
            </>
          ) : (
            <>
              support@knowblemind.com
            </>
          )}
        </motion.button>
        <p className={styles.about__contactHint}>
          Click to copy email address
        </p>
      </motion.section>
    </div>
  );
}
